import AddIcon from '@mui/icons-material/Add'
import CloseIcon from '@mui/icons-material/Close'
import { Box, ButtonBase, IconButton, Typography } from '@mui/material'
import React, { forwardRef } from 'react'

/**
 * Shared building blocks for the patient form. Every visual decision (input height, label
 * weight, colours, grid rhythm) lives here so the create / edit / view surfaces on all
 * portals look identical.
 */

export const PATIENT_FORM_TOKENS = {
  controlHeight: 40,
  // Mobile browsers: a 44px target is what fingers and iOS zoom rules expect.
  controlHeightMobile: 44,
  radius: 8,
  gap: 12,
  rowGap: 14,
  text: '#0f172a',
  label: '#334155',
  muted: '#64748b',
  border: '#cbd5e1',
  borderHover: '#94a3b8',
  accent: '#4f5fd8',
  accentSoft: 'rgba(103, 121, 227, 0.12)',
  accentRing: 'rgba(103, 121, 227, 0.22)',
  error: '#b91c1c',
  errorSoft: 'rgba(185, 28, 28, 0.08)',
  success: '#15803d',
  disabledBg: '#f8fafc',
  disabledText: '#475569',
  surface: '#ffffff',
  surfaceSoft: '#f8fafc',
}

const T = PATIENT_FORM_TOKENS

export const patientControlSx = {
  width: '100%',
  minWidth: 0,
  height: { xs: `${T.controlHeightMobile}px`, sm: `${T.controlHeight}px` },
  boxSizing: 'border-box',
  padding: '0 12px',
  // 16px on phones stops iOS Safari from zooming the page when a field is focused.
  fontSize: { xs: '16px', sm: '14px' },
  lineHeight: { xs: `${T.controlHeightMobile - 2}px`, sm: `${T.controlHeight - 2}px` },
  color: T.text,
  background: T.surface,
  border: `1px solid ${T.border}`,
  borderRadius: `${T.radius}px`,
  outline: 'none',
  transition: 'border-color 0.15s ease, box-shadow 0.15s ease, background 0.15s ease',
  appearance: 'none',
  WebkitAppearance: 'none',
  '&::placeholder': { color: '#94a3b8', opacity: 1 },
  '&:hover:not(:disabled)': { borderColor: T.borderHover },
  '&:focus': { borderColor: T.accent, boxShadow: `0 0 0 3px ${T.accentRing}` },
  '&:disabled': {
    background: T.disabledBg,
    color: T.disabledText,
    borderColor: '#e2e8f0',
    cursor: 'default',
  },
  '&[aria-invalid="true"]': { borderColor: T.error },
  '&[aria-invalid="true"]:focus': { boxShadow: `0 0 0 3px ${T.errorSoft}` },
  // Required-but-empty: soft red tint (label turns red too) until the field is filled.
  '.patient-field--required-empty &:not(:focus)': { background: '#fff5f5', borderColor: '#fca5a5' },
}

/** 12-column grid used by every section of the form. */
export const PatientFormGrid = ({ children, sx, ...rest }) => (
  <Box
    sx={{
      display: 'grid',
      gridTemplateColumns: 'repeat(12, minmax(0, 1fr))',
      columnGap: `${T.gap}px`,
      rowGap: `${T.rowGap}px`,
      width: '100%',
      minWidth: 0,
      ...sx,
    }}
    {...rest}
  >
    {children}
  </Box>
)

function spanToSx(span) {
  if (typeof span === 'number') {
    return { gridColumn: `span ${span}` }
  }
  const { xs = 12, sm, md, lg } = span || {}
  const gridColumn = { xs: `span ${xs}` }
  if (sm) gridColumn.sm = `span ${sm}`
  if (md) gridColumn.md = `span ${md}`
  if (lg) gridColumn.lg = `span ${lg}`
  return { gridColumn }
}

/** A grid cell. `span` is a number or `{ xs, sm, md, lg }`. */
export const PatientFormCell = ({ span = { xs: 12, md: 6 }, children, sx, ...rest }) => (
  <Box sx={{ minWidth: 0, ...spanToSx(span), ...sx }} {...rest}>
    {children}
  </Box>
)

export const PatientFieldLabel = ({ children, required = false, htmlFor, sx, style, emphasis = false }) => (
  <Typography
    component="label"
    htmlFor={htmlFor}
    sx={{
      display: 'block',
      marginBottom: '5px',
      fontSize: '12.5px',
      lineHeight: 1.3,
      fontWeight: 600,
      color: emphasis ? T.error : T.label,
      whiteSpace: 'normal',
      wordBreak: 'break-word',
      ...sx,
    }}
    style={style}
  >
    {children}
    {required && (
      <Box component="span" aria-hidden sx={{ color: T.error, marginLeft: '2px' }}>
        *
      </Box>
    )}
  </Typography>
)

export const PatientFieldHint = ({ children, tone = 'muted', sx }) => {
  if (!children) return null
  const color = tone === 'error' ? T.error : tone === 'success' ? T.success : T.muted
  return (
    <Box
      role={tone === 'error' ? 'alert' : undefined}
      sx={{
        marginTop: '4px',
        fontSize: '12px',
        lineHeight: 1.4,
        color,
        ...sx,
      }}
    >
      {children}
    </Box>
  )
}

/**
 * Label + control + one optional hint / error line. The error wins over the hint so the
 * field never grows by more than a single line.
 */
export const PatientFormField = ({
  label,
  required = false,
  hint,
  hintTone = 'muted',
  error,
  htmlFor,
  span = { xs: 12, md: 6 },
  children,
  sx,
  labelAddon,
  footer,
  requiredEmpty = false,
}) => (
  <PatientFormCell
    span={span}
    sx={sx}
    className={requiredEmpty ? 'patient-field--required-empty' : undefined}
    data-required-empty={requiredEmpty ? 'true' : undefined}
  >
    {label && (
      <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 1 }}>
        <PatientFieldLabel required={required} htmlFor={htmlFor} emphasis={requiredEmpty}>
          {label}
        </PatientFieldLabel>
        {labelAddon}
      </Box>
    )}
    {children}
    {error ? (
      <PatientFieldHint tone="error">{error}</PatientFieldHint>
    ) : (
      <PatientFieldHint tone={hintTone}>{hint}</PatientFieldHint>
    )}
    {footer}
  </PatientFormCell>
)

export const PatientTextInput = forwardRef(function PatientTextInput(
  { invalid = false, sx, ...props },
  ref,
) {
  return (
    <Box
      component="input"
      ref={ref}
      aria-invalid={invalid ? 'true' : undefined}
      sx={{ ...patientControlSx, ...sx }}
      {...props}
    />
  )
})

export const PatientNativeSelect = forwardRef(function PatientNativeSelect(
  { options = [], getOptionLabel, getOptionValue, invalid = false, sx, children, ...props },
  ref,
) {
  const labelOf = getOptionLabel || ((option) => option?.label)
  const valueOf = getOptionValue || ((option) => option?.value)
  return (
    <Box sx={{ position: 'relative', width: '100%', minWidth: 0 }}>
      <Box
        component="select"
        ref={ref}
        aria-invalid={invalid ? 'true' : undefined}
        sx={{
          ...patientControlSx,
          paddingRight: '32px',
          cursor: props.disabled ? 'default' : 'pointer',
          ...sx,
        }}
        {...props}
      >
        {children ||
          options.map((option) => (
            <option key={valueOf(option)} value={valueOf(option)}>
              {labelOf(option)}
            </option>
          ))}
      </Box>
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          right: '12px',
          top: '50%',
          width: '8px',
          height: '8px',
          borderRight: `2px solid ${T.muted}`,
          borderBottom: `2px solid ${T.muted}`,
          transform: 'translateY(-70%) rotate(45deg)',
          pointerEvents: 'none',
        }}
      />
    </Box>
  )
})

/** Pill-style single choice (gender). Same height as text inputs. */
export const PatientSegmentedControl = ({
  options = [],
  value,
  onChange,
  disabled = false,
  invalid = false,
  requiredEmpty = false,
  ariaLabel,
}) => (
  <Box
    role="radiogroup"
    aria-label={ariaLabel}
    aria-invalid={invalid ? 'true' : undefined}
    sx={{
      display: 'flex',
      width: '100%',
      height: { xs: `${T.controlHeightMobile}px`, sm: `${T.controlHeight}px` },
      boxSizing: 'border-box',
      border: `1px solid ${invalid ? T.error : requiredEmpty ? '#fca5a5' : T.border}`,
      borderRadius: `${T.radius}px`,
      background: disabled ? T.disabledBg : requiredEmpty ? '#fff5f5' : T.surface,
      overflow: 'hidden',
    }}
  >
    {options.map((option, index) => {
      const selected = option?.id === value
      return (
        <ButtonBase
          key={option?.id}
          role="radio"
          aria-checked={selected}
          disabled={disabled}
          onClick={() => {
            if (!disabled) onChange?.(option?.id)
          }}
          sx={{
            flex: 1,
            minWidth: 0,
            height: '100%',
            fontSize: { xs: '14px', sm: '13.5px' },
            fontWeight: 600,
            color: selected ? '#312e81' : disabled ? T.disabledText : T.label,
            background: selected ? '#e0e7ff' : 'transparent',
            borderLeft: index === 0 ? 'none' : `1px solid ${T.border}`,
            transition: 'background 0.15s ease, color 0.15s ease',
            '&:hover': !disabled && !selected ? { background: T.surfaceSoft } : undefined,
            '&.Mui-focusVisible': { boxShadow: `inset 0 0 0 2px ${T.accentRing}` },
          }}
        >
          {option?.label}
        </ButtonBase>
      )
    })}
  </Box>
)

export const PatientCheckbox = ({
  label,
  checked,
  onChange,
  disabled = false,
  description,
  id,
  sx,
}) => (
  <Box
    component="label"
    htmlFor={id}
    sx={{
      display: 'flex',
      alignItems: 'flex-start',
      gap: '10px',
      cursor: disabled ? 'default' : 'pointer',
      fontSize: '13.5px',
      lineHeight: 1.4,
      color: disabled ? T.disabledText : T.text,
      userSelect: 'none',
      ...sx,
    }}
  >
    <Box
      component="input"
      type="checkbox"
      id={id}
      checked={Boolean(checked)}
      onChange={onChange}
      disabled={disabled}
      sx={{
        width: { xs: '20px', sm: '17px' },
        height: { xs: '20px', sm: '17px' },
        marginTop: '2px',
        flexShrink: 0,
        accentColor: T.accent,
        cursor: disabled ? 'default' : 'pointer',
      }}
    />
    <Box sx={{ minWidth: 0 }}>
      <Box component="span" sx={{ fontWeight: 500 }}>
        {label}
      </Box>
      {description && (
        <Box sx={{ fontSize: '12px', color: T.muted, marginTop: '2px' }}>{description}</Box>
      )}
    </Box>
  </Box>
)

/** Small uppercase group title used inside "More details". */
export const PatientSectionTitle = ({ children, action, sx }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 1,
      marginBottom: '2px',
      ...sx,
    }}
  >
    <Typography
      component="div"
      sx={{
        fontSize: '11.5px',
        fontWeight: 700,
        letterSpacing: '0.05em',
        textTransform: 'uppercase',
        color: T.muted,
      }}
    >
      {children}
    </Typography>
    {action}
  </Box>
)

/** "+ Add alternate number" style text action. */
export const PatientInlineAddButton = ({ children, onClick, disabled = false, sx }) => (
  <ButtonBase
    onClick={onClick}
    disabled={disabled}
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      alignSelf: 'flex-start',
      gap: '4px',
      minHeight: { xs: '36px', sm: '28px' },
      padding: '4px 6px',
      marginLeft: '-6px',
      borderRadius: '6px',
      fontSize: { xs: '13px', sm: '12.5px' },
      fontWeight: 600,
      color: disabled ? T.disabledText : T.accent,
      '&:hover': { background: T.accentSoft },
      '&.Mui-focusVisible': { boxShadow: `0 0 0 3px ${T.accentRing}` },
      ...sx,
    }}
  >
    <AddIcon sx={{ fontSize: '16px' }} />
    {children}
  </ButtonBase>
)

export const PatientRowDeleteButton = ({ onClick, disabled = false, label = 'Remove', sx }) => (
  <IconButton
    size="small"
    aria-label={label}
    title={label}
    onClick={onClick}
    disabled={disabled}
    sx={{
      width: { xs: '40px', sm: '32px' },
      height: { xs: '40px', sm: '32px' },
      color: '#94a3b8',
      '&:hover': { color: T.error, background: T.errorSoft },
      ...sx,
    }}
  >
    <CloseIcon sx={{ fontSize: '18px' }} />
  </IconButton>
)

export const PatientChip = ({ children, tone = 'accent', sx }) => (
  <Box
    component="span"
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      fontSize: '11px',
      fontWeight: 600,
      lineHeight: 1,
      padding: '4px 8px',
      borderRadius: '999px',
      whiteSpace: 'nowrap',
      color: tone === 'accent' ? '#3730a3' : T.muted,
      background: tone === 'accent' ? '#e0e7ff' : '#f1f5f9',
      ...sx,
    }}
  >
    {children}
  </Box>
)

/** Soft-bordered container for grouped repeatable rows (attendant cards, related-patient box). */
export const PatientCard = ({ children, tone = 'default', sx, ...rest }) => (
  <Box
    {...rest}
    sx={{
      padding: '12px',
      borderRadius: '10px',
      border: `1px solid ${tone === 'accent' ? 'rgba(103, 121, 227, 0.28)' : '#e2e8f0'}`,
      background: tone === 'accent' ? 'rgba(103, 121, 227, 0.06)' : T.surfaceSoft,
      minWidth: 0,
      ...sx,
    }}
  >
    {children}
  </Box>
)

export const PatientInfoBanner = ({ children, sx }) => (
  <Box
    sx={{
      padding: '10px 12px',
      borderRadius: '8px',
      fontSize: '13px',
      lineHeight: 1.45,
      color: '#1e3a8a',
      background: '#eff6ff',
      border: '1px solid #bfdbfe',
      ...sx,
    }}
  >
    {children}
  </Box>
)
